import { Button, Center, Grid, useMantineTheme } from '@mantine/core'
import { useQueryClient } from '@tanstack/react-query'
import { getDeviceIndicatorsOptionsHelper } from 'api/devices'
import { getModelIndicatorsOptionsHelper } from 'api/models/getModelIndicators'
import { getDeviceOidsIndicatorsHelper } from 'api/snmpOids/getSnmpDeviceOids'
import {
  useDeviceModelContext,
  useDeviceTypeContext,
} from 'components/devices/DeviceIndicators'
import { useMemo, useState } from 'react'
import { FormProvider } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { BiRename } from 'react-icons/bi'
import { FiType } from 'react-icons/fi'
import { IoClose } from 'react-icons/io5'
import { TbPlus } from 'react-icons/tb'
import { TiWeatherPartlySunny } from 'react-icons/ti'
import { useParams } from 'react-router-dom'
import { capitalizeFirstLetter } from 'shared/utils'
import {
  IconButton,
  InputAsyncSelect,
  InputSelect,
  InputSubmit,
  InputText,
} from '..'
import { columnTypeOptions } from '../../data/columnType'
import { useDashboardContext } from './DashboardProvider'
import { useDashboardStore } from './hooks/useDashboardStore'
import { useInsertColumn } from './hooks/useInsertColumn'
import { calculateRemainingSpace } from './utils/calculateRemainingSpace'
import { dashboardColumnSize } from './utils/dashboardColumnSize'

export const AddColumnButton = ({ rowId }: { rowId: string }) => {
  const dashboard = useDashboardContext()
  const isEditMode = useDashboardStore(state => state.isEditMode)
  const [isFormView, setFormView] = useState(false)
  const { t } = useTranslation()
  const theme = useMantineTheme()
  const defaultVariant = theme.fn.variant({ variant: 'default' })
  const SMALLEST_COLUMN = dashboardColumnSize.get('OneOnThree')!.colSpan
  const hasAvailableSpace = useMemo(
    () =>
      calculateRemainingSpace(
        dashboard.rows.find(row => row.id === rowId)!.columns,
      ) >= SMALLEST_COLUMN,
    [dashboard],
  )

  const exitInsertMode = () => setFormView(false)

  if (!isEditMode || !hasAvailableSpace) return null

  return (
    <Grid.Col span={2}>
      {isFormView ? (
        <Center
          style={{
            minHeight: 350,
            border: `1px dashed ${defaultVariant.border}`,
            backgroundColor: defaultVariant.background,
            color: defaultVariant.color,
            borderRadius: theme.radius.md,
            position: 'relative',
          }}
        >
          <>
            <AddColumnForm onSuccess={exitInsertMode} rowId={rowId} />
            <ExitInsertMode onExit={exitInsertMode} />
          </>
        </Center>
      ) : (
        <Button
          style={{
            minHeight: 350,
            borderStyle: 'dashed',
          }}
          fullWidth
          size="xl"
          variant="default"
          leftSection={<TbPlus />}
          onClick={() => setFormView(true)}
        >
          {t('dashboard.insertColumn')}
        </Button>
      )}
    </Grid.Col>
  )
}

const AddColumnForm = ({
  rowId,
  onSuccess,
}: {
  rowId: string
  onSuccess: () => void
}) => {
  const { t } = useTranslation()
  const { deviceId } = useParams()
  const queryClient = useQueryClient()

  if (!deviceId) throw new Error('Missing deviceId')

  const { methods, onSubmit } = useInsertColumn({
    rowId,
    handleSuccess: () => {
      queryClient.refetchQueries('dashboards' as unknown as readonly unknown[])
      onSuccess()
    },
  })

  const indicatorHelper = useGetIndicatorsList({ deviceId })

  return (
    <FormProvider {...methods}>
      <form onSubmit={onSubmit}>
        <InputText
          name="name"
          label={capitalizeFirstLetter(t('generic.name'))}
          icon={BiRename}
        />
        <InputSelect
          name="type"
          label={capitalizeFirstLetter(t('generic.type'))}
          data={columnTypeOptions(t)}
          icon={FiType}
        />
        <InputAsyncSelect
          name="indicator"
          label={capitalizeFirstLetter(t('sensor.indicator'))}
          icon={TiWeatherPartlySunny}
          {...indicatorHelper}
        />
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            marginTop: '8px',
          }}
        >
          <InputSubmit value={t('generic.add')} />
        </div>
      </form>
    </FormProvider>
  )
}

const ExitInsertMode = ({ onExit }: { onExit: () => void }) => {
  const { t } = useTranslation()
  return (
    <IconButton
      style={{ position: 'absolute', top: '1em', right: '1em' }}
      onClick={onExit}
      icon={IoClose}
      label={t('generic.cancel')}
    />
  )
}

const useGetIndicatorsList = ({ deviceId }: { deviceId: string }) => {
  const { t } = useTranslation()
  const deviceModel = useDeviceModelContext()
  const deviceType = useDeviceTypeContext()

  return useMemo(() => {
    let indicatorHelper
    switch (deviceType) {
      case 'SNMP':
        indicatorHelper = getDeviceOidsIndicatorsHelper(deviceId)
        break
      default:
        indicatorHelper = deviceModel
          ? getModelIndicatorsOptionsHelper(deviceModel, t)
          : getDeviceIndicatorsOptionsHelper(deviceId!, t)
        break
    }
    return indicatorHelper
  }, [deviceType, deviceId, deviceModel])
}
